import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import '@/App.css';

// Components
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GoogleAdsTracking from '@/components/GoogleAdsTracking';
import EasterEgg from '@/components/EasterEgg';
import MainLanding from '@/pages/MainLanding';
import HyperGrid from '@/pages/HyperGrid';
import LandOffer from '@/pages/LandOffer';
import AdminLandOffers from '@/pages/AdminLandOffers';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Dashboard from '@/pages/Dashboard';
import Impressum from '@/pages/Impressum';
import Datenschutz from '@/pages/Datenschutz';
import ConsentBanner from '@/components/ConsentBanner';
import ProjectBar from '@/components/ProjectBar';
import Info from '@/pages/Info';
import VerifyEmail from '@/pages/VerifyEmail';
import EditProject from '@/pages/EditProject';
import AdminUsers from '@/pages/AdminUsers';
import AdminSettings from '@/pages/AdminSettings';
import AdminContent from '@/pages/AdminContent';
import Profile from '@/pages/Profile';
import DatabaseManagement from '@/pages/DatabaseManagement';
import DatabaseBackup from '@/pages/DatabaseBackup';
import ReCaptchaSettings from '@/pages/ReCaptchaSettings';
import ResetPassword from '@/pages/ResetPassword';
import DirectInvestment from '@/pages/DirectInvestment';
import PlatformManagement from '@/pages/PlatformManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Helper function to check if JWT token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiration and if it's expired
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    }
    
    // If no expiration in token, consider it valid
    return false;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;  // If we can't decode, consider it expired
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorIdRef = useRef(null);

  useEffect(() => {
    // Migration: Move old 'access_token' to new 'token' key if exists
    const oldToken = localStorage.getItem('access_token');
    if (oldToken) {
      localStorage.setItem('token', oldToken);
      localStorage.removeItem('access_token');
      console.log('Migrated token from access_token to token');
    }
    
    // Check for existing token
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token is expired before using it
      if (isTokenExpired(token)) {
        console.log('Token is expired, clearing authentication state');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
      } else {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchCurrentUser();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token'); // Also remove old token key if exists
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    
    // Show logout message for 3 seconds
    toast.success('Sie wurden erfolgreich ausgeloggt', {
      duration: 3000,
    });
  };

  // Setup axios interceptor for global 401 handling
  useEffect(() => {
    // Setup response interceptor
    const interceptorId = axios.interceptors.response.use(
      (response) => {
        // If response is successful, just return it
        return response;
      },
      (error) => {
        // Handle 401 Unauthorized errors globally
        if (error.response && error.response.status === 401) {
          // Check if we're already on the login page to avoid infinite loops
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/login')) {
            console.error('Session expired or unauthorized. Logging out...');
            
            // Clear authentication state
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            
            // Show error message
            toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
            
            // Redirect to login page
            window.location.href = '/bess/login';
          }
        }
        
        // Always reject the promise so the calling code can handle it
        return Promise.reject(error);
      }
    );

    // Store interceptor ID in ref
    interceptorIdRef.current = interceptorId;

    // Cleanup function to remove interceptor when component unmounts
    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.response.eject(interceptorIdRef.current);
      }
    };
  }, []);  // Empty dependency array - only setup once on mount

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-pulse text-xl font-semibold text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <GoogleAdsTracking />
        <ConsentBanner />
        <ProjectBar current="bess" />
        <EasterEgg />
        <div className="App min-h-screen flex flex-col bg-slate-50">
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Main Landing Page */}
            <Route path="/" element={<MainLanding />} />
            <Route path="/hyper-grid" element={<HyperGrid />} />
            <Route path="/flaeche-anbieten" element={<LandOffer />} />
            
            {/* BESS Project Routes */}
            <Route path="/bess/*" element={
              <>
                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={!user ? <Login /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/register" element={!user ? <Register /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/verify/:token" element={<VerifyEmail />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/projects/:id/edit" element={user && ['admin', 'content_filler'].includes(user.role) ? <EditProject /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/bess/login" />} />
                    <Route path="/profile" element={user ? <Profile /> : <Navigate to="/bess/login" />} />
                    <Route path="/admin/users" element={user && (user.role === 'admin' || user.role === 'superadmin') ? <AdminUsers /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/admin/settings" element={user && (user.role === 'admin' || user.role === 'superadmin') ? <AdminSettings /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/admin/land-offers" element={user && (user.role === 'admin' || user.role === 'superadmin') ? <AdminLandOffers /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/admin/content" element={user && (user.role === 'admin' || user.role === 'superadmin') ? <AdminContent /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/admin/database" element={user && user.role === 'superadmin' ? <DatabaseManagement /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/superadmin/backup" element={user && user.role === 'superadmin' ? <DatabaseBackup /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/superadmin/recaptcha-settings" element={user && user.role === 'superadmin' ? <ReCaptchaSettings /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/superadmin/direct-investment" element={user && user.role === 'superadmin' ? <DirectInvestment /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/superadmin/platform" element={user && user.role === 'superadmin' ? <PlatformManagement /> : <Navigate to="/bess/dashboard" />} />
                    <Route path="/impressum" element={<Impressum />} />
                    <Route path="/datenschutz" element={<Datenschutz />} />
                    <Route path="/info" element={<Info />} />
                  </Routes>
                </main>
                <Footer />
              </>
            } />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
